/**
 * Typen des Reiters „Zutritt, Fluchttür, Türüberwachung" auf der Seite „Brandfallsteuerung und Notstrom"
 * (W2-D3, 17.10.2026).
 *
 * Die Typen folgen `GET …/elektro/sicherheitstechnik/zutritt` (SicherheitstechnikEndpoints.cs,
 * Zutrittslauf.cs, Zutrittskern.cs). Gerechnet wird beim Abruf; die Seite schreibt nichts.
 */
import type { StVorgabe } from './sicherheitstechnik';

export const ZUTRITT_HINWEIS =
  'Abgleich, kein Nachweis: „erfüllt" heißt nur, dass ein Bauteil der Art an der Tür steht. '
  + 'Gefordert ist eine Tür, wenn das Sicherheitskonzept oder das Türbuch es sagt. Die Türüberwachung läuft nach '
  + 'R15 über die Zutrittskontrolle, eine eigenständige EMA gibt es nicht. „Nicht geplant": Für Sprechstellen '
  + 'kennt das Modell keine Bauteilart. Ob das ZK-System die Überwachung leistet (ELT-54, ELT-62) und Funk-Türblatt '
  + 'gegen verdrahteten Leser (ELT-97) entscheidet die Planung.';

export const ZUTRITT_ANNAHMEN_HINWEIS =
  'Die Zuordnung Bauteil → Tür (Umkreis, Vorsprung, Höhenfenster, Raumprobe) und die Leistung je '
  + 'Fluchttürterminal stehen auf unbestätigten Vorgaben (Migration 1040).';

export interface ZutrittKennzahlen {
  tueren: number; tuerenImAbgleich: number; tuerenOhneNummer: number; leser: number; terminals: number;
  zugeordnet: number; ohneTuer: number; gespeichertAbweichend: number; ohneVersorgung: number;
  verteilerOhneKreis: number; svGefordert: number; svAusNormalnetz: number; zutrittFunk: number;
  befunde: number; warnungen: number; fehler: number; aufAnnahmen: boolean;
}

export interface ZutrittMatrixzeile {
  gewerk: string; titel: string; bauteileImModell: number; gefordertKonzept: number; gefordertTuerbuch: number;
  gefordert: number; erfuellt: number; fehlt: number; nichtGeplant: number; ohneAnforderung: number;
  quellenWidersprechen: number;
}

export interface ZutrittTuer {
  tuer: string; geschoss: string | null; raum: string | null; tuerklasse: string | null;
  zutritt: string; zutrittQuelle: string; zutrittBauart: string | null;
  fluchttuer: string; fluchttuerQuelle: string;
  tuerueberwachung: string; tuerueberwachungQuelle: string;
  sprechstelle: string; sprechstelleQuelle: string;
  leser: number; terminals: number; bauteile: string | null; stromversorgung: string | null; versorgung: string;
  befunde: string[];
}

export interface ZutrittBauteil {
  bauteilart: string; kennung: string; familie: string | null; typ: string | null; geschoss: string | null;
  raum: string | null; tuer: string | null; zuordnung: string; abstandM: number | null; zweiteM: number | null;
  tuerGespeichert: string | null; tuerGespeichertHerkunft: string | null; gespeichertWeichtAb: boolean;
  verteiler: string | null; verteilerArt: string | null; versorgungsart: string | null; stromkreis: string | null;
  versorgung: string;
}

export interface ZutrittVersorgung {
  verteiler: string; art: string | null; versorgungsart: string | null; leser: number; terminals: number;
  mitStromkreis: number; poe: number; ohneStromkreis: number; terminalleistungW: number;
}

export interface ZutrittStand {
  kennzahlen: ZutrittKennzahlen;
  matrix: ZutrittMatrixzeile[];
  tueren: ZutrittTuer[];
  bauteile: ZutrittBauteil[];
  versorgung: ZutrittVersorgung[];
  vorgaben: StVorgabe[];
  befunde: string[];
  stand: string;
}

/** Der Status je Tür und Gewerk als Wort (Zutrittskern.Status). */
export function statusname(status: string): string {
  switch (status) {
    case 'erfuellt': return 'erfüllt';
    case 'fehlt': return 'fehlt';
    case 'nicht_geplant': return 'nicht geplant';
    case 'ohne_anforderung': return 'ohne Anforderung';
    default: return '—';
  }
}

/** Die Versorgung eines Bauteils als Wort (Zutrittskern.Versorgung). */
export function versorgungsname(versorgung: string): string {
  switch (versorgung) {
    case 'stromkreis': return 'Stromkreis';
    case 'poe': return 'PoE (IT-Verteiler)';
    case 'verteiler_ohne_kreis': return 'Verteiler ohne Stromkreis';
    case 'ohne_versorgung': return 'ohne Versorgung';
    case 'kein Bauteil': return 'kein Bauteil';
    default: return versorgung;
  }
}
